import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import UIMock from "@/studio/components/UIMock";
import Nav from "@/studio/sections/Nav";
import Atmosphere from "@/studio/components/Atmosphere";
import ContactModal from "@/studio/components/ContactModal";
import { useStudioScroll } from "@/studio/lib/useStudioScroll";
import { useTheme } from "@/studio/lib/theme";

const ease = [0.16, 1, 0.3, 1];

/* ============================================================
 * Content
 * ============================================================ */

const SERVICES = [
  {
    n: "01",
    slug: "websites",
    title: "Websites",
    headline: "Sites that shape perception.",
    desc: "Editorial, fast and unforgettable. Built to make the first impression count — and to convert.",
    mock: "website",
    usecases: [
      "Company sites for serious teams",
      "Product launches and landing pages",
      "Editorial content hubs",
      "Immersive brand experiences",
    ],
    capabilities: [
      "Custom design and art direction",
      "CMS integration (Sanity / Strapi / Custom)",
      "Performance, SEO and Core Web Vitals",
      "Multi-language and localisation",
      "Analytics, A/B and conversion tooling",
    ],
    stack: ["Next.js", "Tailwind", "Framer Motion", "GSAP", "Vercel", "Cloudflare"],
    examples: ["Parabolum Studio", "Editorial product launches", "B2B marketing rebuilds"],
    timeline: "3 – 6 weeks",
  },
  {
    n: "02",
    slug: "platforms",
    title: "Platforms",
    headline: "Products built around real users.",
    desc: "SaaS, dashboards, portals and internal systems engineered to grow with your team and your data.",
    mock: "dashboard",
    usecases: [
      "SaaS products from MVP to scale",
      "Customer-facing portals",
      "Multi-tenant systems with billing",
      "Internal admin and operations tools",
    ],
    capabilities: [
      "Auth, roles and permissions",
      "Subscriptions and billing (Stripe)",
      "Realtime UI (websockets)",
      "Background jobs and queues",
      "Audit logs and observability",
    ],
    stack: ["React", "FastAPI", "PostgreSQL", "MongoDB", "Redis", "Stripe"],
    examples: ["B2B SaaS dashboards", "Customer ops portals", "Marketplace backends"],
    timeline: "6 – 12 weeks",
  },
  {
    n: "03",
    slug: "ai-systems",
    title: "AI Systems",
    headline: "Real intelligence in your workflow.",
    desc: "Assistants, copilots, voice and agents — wired directly into the systems your team already uses.",
    mock: "ai",
    usecases: [
      "Customer-facing assistants",
      "Internal copilots that draft, score, route",
      "Voice agents for inbound / outbound",
      "Autonomous agents with tool access",
    ],
    capabilities: [
      "RAG over your private knowledge base",
      "Tool / function calling (CRM, email, calendars)",
      "Streaming UI with token-level control",
      "Eval, guardrails and cost monitoring",
      "Self-hosting or managed LLM routing",
    ],
    stack: ["GPT-5.2", "Claude Sonnet 4.5", "Gemini 3", "FastAPI", "Vector DB", "n8n"],
    examples: ["LeadForge enrichment", "Sales copilot agents", "Operations triage AI"],
    timeline: "4 – 10 weeks",
  },
  {
    n: "04",
    slug: "internal-tools",
    title: "Internal Tools",
    headline: "Tools your team actually wants to use.",
    desc: "Custom CRMs, ERPs, admin systems and analytics dashboards — designed around your real process.",
    mock: "table",
    usecases: [
      "CRMs tailored to your sales motion",
      "ERPs that match your operations",
      "Admin panels for non-technical teams",
      "Live analytics and KPI dashboards",
    ],
    capabilities: [
      "Custom data models and workflows",
      "Bulk operations and CSV / Excel sync",
      "SSO and role-based access",
      "Reports, exports and scheduled emails",
      "Direct integrations to your stack",
    ],
    stack: ["React", "FastAPI", "PostgreSQL", "Metabase", "Retool patterns"],
    examples: ["Sales CRMs", "Logistics ERPs", "Internal ops dashboards"],
    timeline: "5 – 10 weeks",
  },
  {
    n: "05",
    slug: "automation",
    title: "Automation",
    headline: "Systems that quietly do the work.",
    desc: "Lead pipelines, integrations, outreach engines and monitoring — running 24/7 so your team doesn't have to.",
    mock: "pipeline",
    usecases: [
      "Outbound and enrichment pipelines",
      "Lifecycle email and CRM sync",
      "Webhook-driven business logic",
      "Cross-tool data sync (Stripe ↔ HubSpot ↔ Notion)",
      "Uptime and SLA monitoring",
    ],
    capabilities: [
      "n8n / Zapier / custom workers",
      "Retries, idempotency, dead-letter queues",
      "Alerting via email, Slack, Telegram",
      "Audit trails for every action",
      "Rate-limit aware integrations",
    ],
    stack: ["n8n", "FastAPI", "Redis", "Webhooks", "Cron", "Sentry"],
    examples: ["LeadForge enrichment runs", "RevOps sync flows", "Monitoring + alert stacks"],
    timeline: "2 – 6 weeks",
  },
];

const PROCESS = [
  { n: "01", title: "Discovery", body: "We listen, map your real problem, and decide whether we're a good fit." },
  { n: "02", title: "Architecture", body: "We design the stack, the data model and the build sequence — written, not vibes." },
  { n: "03", title: "Build", body: "Direct, focused execution. Weekly demos. No meetings for the sake of meetings." },
  { n: "04", title: "Launch", body: "Production deploy, instrumentation, and a runbook your team can own." },
  { n: "05", title: "Care", body: "Quiet retainers — we keep the product sharp years after launch." },
];

const FAQ = [
  {
    q: "Do you work fixed-price or by retainer?",
    a: "Both. Most projects are scoped fixed for the first build and move into a quiet monthly retainer once they're live.",
  },
  {
    q: "How small is too small?",
    a: "If it ships in under 2 weeks and you can build it on Webflow, you probably don't need us. We're best for products with real engineering behind them.",
  },
  {
    q: "Can we own the code?",
    a: "Always. Code lives in your GitHub from day one. We deliver clean, documented, deployable repos.",
  },
  {
    q: "Do you use AI in your own work?",
    a: "Yes — to ship faster and review more rigorously. Code review and final calls stay human.",
  },
  {
    q: "Can you join an existing codebase?",
    a: "We take selective audits and rescue projects when the fit is right. Honest assessment first.",
  },
];

/* ============================================================
 * Building blocks
 * ============================================================ */

function SectionLabel({ n, label }) {
  return (
    <span className="ff-ui label text-[var(--ink-soft)]">
      ({n}) — {label}
    </span>
  );
}

function ServiceBlock({ s, flip }) {
  return (
    <article
      data-testid={`service-detail-${s.slug}`}
      className="grid grid-cols-12 items-start gap-8 border-t border-[var(--line)] py-20 md:gap-12 md:py-28"
    >
      {/* Visual */}
      <div
        className={`col-span-12 lg:col-span-7 ${flip ? "lg:order-2" : ""}`}
      >
        <div className="relative aspect-[16/11] overflow-hidden rounded-[28px] border border-white/[0.08] bg-[#070708] shadow-[0_40px_120px_-40px_rgba(0,0,0,0.85)]">
          <div className="absolute inset-8 md:inset-10">
            <UIMock kind={s.mock} className="h-full" />
          </div>
        </div>
      </div>

      {/* Copy */}
      <div className={`col-span-12 lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
        <div className="ff-ui text-[11px] font-medium tracking-[0.28em] text-[var(--ink-faint)]">
          {s.n} — Service
        </div>
        <h3 className="ff-display mt-3 text-4xl font-extrabold leading-[0.98] tracking-tight md:text-5xl">
          {s.title}
        </h3>
        <p className="ff-serif mt-5 max-w-md text-xl leading-snug text-[var(--ink-soft)] md:text-2xl">
          {s.headline}
        </p>
        <p className="mt-5 max-w-md text-[15px] leading-relaxed text-[var(--ink-soft)]">
          {s.desc}
        </p>

        <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2">
          <div>
            <div className="ff-ui text-[10px] font-medium tracking-[0.28em] text-[var(--ink-faint)]">
              Use cases
            </div>
            <ul className="mt-3 space-y-2">
              {s.usecases.map((u) => (
                <li key={u} className="text-[13.5px] leading-snug text-[var(--ink)]/85">
                  {u}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="ff-ui text-[10px] font-medium tracking-[0.28em] text-[var(--ink-faint)]">
              Capabilities
            </div>
            <ul className="mt-3 space-y-2">
              {s.capabilities.map((c) => (
                <li key={c} className="text-[13.5px] leading-snug text-[var(--ink)]/85">
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t border-[var(--line)] pt-6">
          <div className="ff-ui text-[10px] font-medium tracking-[0.28em] text-[var(--ink-faint)]">
            Recommended stack
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {s.stack.map((t) => (
              <span
                key={t}
                className="ff-ui rounded-full border border-[var(--line)] px-3 py-1 text-[11px] text-[var(--ink-soft)]"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 text-[13px] text-[var(--ink-soft)]">
          <div>
            <div className="ff-ui text-[10px] tracking-[0.28em] text-[var(--ink-faint)]">
              Example projects
            </div>
            <div className="mt-2 leading-snug text-[var(--ink)]/85">
              {s.examples.join(" · ")}
            </div>
          </div>
          <div>
            <div className="ff-ui text-[10px] tracking-[0.28em] text-[var(--ink-faint)]">
              Typical timeline
            </div>
            <div className="mt-2 leading-snug text-[var(--ink)]/85">{s.timeline}</div>
          </div>
        </div>
      </div>
    </article>
  );
}

function FAQRow({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-[var(--line)]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-cursor="big"
        data-testid={`faq-${item.q.slice(0, 20)}`}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-6 py-6 text-left transition-colors"
      >
        <span className="ff-display max-w-2xl text-lg font-semibold leading-snug text-[var(--ink)] md:text-xl">
          {item.q}
        </span>
        <ChevronDown
          className={`mt-1 h-4 w-4 flex-shrink-0 text-[var(--ink-soft)] transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>
      <div
        className="grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="min-h-0">
          <p className="pb-7 pr-10 text-[14px] leading-relaxed text-[var(--ink-soft)]">
            {item.a}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * Page
 * ============================================================ */

export default function ServicesPage() {
  const navigate = useNavigate();
  useStudioScroll();
  const { theme } = useTheme();
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      className="studio-root relative min-h-screen overflow-x-hidden bg-[var(--paper)] text-[var(--ink)]"
      data-theme={theme}
    >
      <Atmosphere />
      <Nav onContact={() => setContactOpen(true)} ready />

      {/* Hero */}
      <section
        data-nav-dark
        className="relative px-5 pt-40 pb-24 md:px-10 md:pt-48 md:pb-32"
      >
        <div className="mx-auto max-w-[1500px]">
          <SectionLabel n="00" label="Services" />
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease }}
            className="ff-display mt-8 text-6xl font-extrabold leading-[0.92] tracking-tight md:text-8xl"
          >
            What we build,
            <br />
            in detail.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 0.1 }}
            className="ff-serif mt-8 max-w-xl text-xl leading-snug text-[var(--ink-soft)] md:text-2xl"
          >
            Five capabilities. One studio. Everything engineered end-to-end by the
            people who designed it.
          </motion.p>

          <div className="mt-12 grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-5">
            {SERVICES.map((s) => (
              <a
                key={s.slug}
                href={`#${s.slug}`}
                data-cursor="big"
                className="group flex flex-col gap-2 border-t border-[var(--line)] pt-4 transition-colors hover:border-[var(--ink)]"
              >
                <span className="ff-ui text-[10px] font-medium tracking-[0.28em] text-[var(--ink-faint)]">
                  {s.n}
                </span>
                <span className="ff-display text-base font-semibold text-[var(--ink)] md:text-lg">
                  {s.title}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Service detail blocks */}
      <section data-nav-dark className="px-5 md:px-10">
        <div className="mx-auto max-w-[1500px]">
          <SectionLabel n="01" label="Capabilities, in depth" />
          {SERVICES.map((s, i) => (
            <div key={s.slug} id={s.slug}>
              <ServiceBlock s={s} flip={i % 2 === 1} />
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section data-nav-dark className="px-5 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-[1500px]">
          <SectionLabel n="02" label="Process" />
          <h2 className="ff-display mt-8 text-5xl font-extrabold leading-[0.95] tracking-tight md:text-7xl">
            Built carefully.
            <br />
            <span className="text-[var(--ink-soft)]">Five steps.</span>
          </h2>

          <div className="mt-16 grid grid-cols-1 gap-10 md:grid-cols-5 md:gap-8">
            {PROCESS.map((p) => (
              <div key={p.n} className="flex flex-col gap-3 border-t border-[var(--line)] pt-6">
                <span className="ff-ui text-[10px] font-medium tracking-[0.28em] text-[var(--ink-faint)]">
                  {p.n}
                </span>
                <h4 className="ff-display text-2xl font-extrabold tracking-tight text-[var(--ink)]">
                  {p.title}
                </h4>
                <p className="text-[13.5px] leading-relaxed text-[var(--ink-soft)]">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Architect integration */}
      <section data-nav-dark className="px-5 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-[1500px]">
          <div className="rounded-[32px] border border-[var(--line)] bg-[var(--paper-2)] p-10 md:p-14">
            <SectionLabel n="03" label="Not sure where you fit?" />
            <h3 className="ff-display mt-6 text-4xl font-extrabold leading-[0.98] tracking-tight md:text-6xl">
              Try the Architect.
            </h3>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-[var(--ink-soft)] md:text-[16px]">
              Answer three short questions and we&apos;ll show you the exact stack
              we&apos;d recommend — with the reasoning behind every choice.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  navigate("/");
                  setTimeout(() => {
                    const el = document.getElementById("architect");
                    if (el) el.scrollIntoView({ behavior: "smooth" });
                  }, 80);
                }}
                data-cursor="big"
                data-testid="services-architect-cta"
                className="group inline-flex items-center gap-2.5 rounded-full bg-[var(--ink)] px-6 py-3.5 text-[14px] font-semibold text-[var(--ink-on)] transition-transform hover:scale-[1.02]"
              >
                <span>Open the Architect</span>
                <span className="transition-transform duration-300 group-hover:translate-x-1">
                  →
                </span>
              </button>
              <button
                type="button"
                onClick={() => setContactOpen(true)}
                data-cursor="big"
                className="inline-flex items-center gap-2.5 rounded-full border border-[var(--ink)]/20 px-6 py-3.5 text-[14px] font-semibold text-[var(--ink)] transition-colors hover:border-[var(--ink)]"
              >
                Or just talk to us
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-nav-dark className="px-5 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-[1500px]">
          <SectionLabel n="04" label="Frequently asked" />
          <h2 className="ff-display mt-8 text-5xl font-extrabold leading-[0.95] tracking-tight md:text-7xl">
            FAQ
          </h2>
          <div className="mt-14 max-w-3xl">
            {FAQ.map((item) => (
              <FAQRow key={item.q} item={item} />
            ))}
            <div className="border-t border-[var(--line)]" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-nav-dark className="px-5 py-32 md:px-10 md:py-40">
        <div className="mx-auto max-w-[1500px]">
          <SectionLabel n="05" label="Let's build" />
          <h2 className="ff-display mt-8 text-6xl font-extrabold leading-[0.92] tracking-tight md:text-8xl">
            Have something
            <br />
            <span className="text-[var(--ink-soft)]">worth building?</span>
          </h2>
          <p className="ff-serif mt-8 max-w-xl text-xl leading-snug text-[var(--ink-soft)] md:text-2xl">
            We take a handful of projects each quarter. If yours sounds like the
            right fit, we&apos;ll reply within a day.
          </p>
          <div className="mt-12 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              data-cursor="big"
              data-testid="services-final-cta"
              className="group inline-flex items-center gap-2.5 rounded-full bg-[var(--ink)] px-6 py-3.5 text-[14px] font-semibold text-[var(--ink-on)] transition-transform hover:scale-[1.02]"
            >
              <span>Start a project</span>
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              data-cursor="big"
              className="inline-flex items-center gap-2.5 rounded-full border border-[var(--ink)]/20 px-6 py-3.5 text-[14px] font-semibold text-[var(--ink)] transition-colors hover:border-[var(--ink)]"
            >
              Back home
            </button>
          </div>
        </div>
      </section>

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}
